<script>
  import { onMount } from "svelte";
  import { browser } from "$app/environment";
  import { marketingConsentStore } from "#lib/stores.js";
  import { resolveConsentRegion } from "#lib/utils/consent-region.js";
  import { captureEvent, captureBeacon } from "#lib/utils/posthog.js";
  import { TIKTOK_PIXEL_ID } from "#lib/utils/load-pixels.js";
  import { signup10Open } from "#lib/stores/signup10.js";

  // Pass false for non-EU visitors to skip the consent banner entirely
  export let requiresCookieConsent = true;
  // The layout renders one auto-showing instance. Manual "manage cookies"
  // re-open widgets (burger menu, cookie policy page) render their own
  // instance purely to call toggleBanner() - they must pass autoShow={false}
  // so they don't ALSO auto-show on first visit. Without this, every page
  // mounted a second banner that ignored geo-gating: two stacked banners for
  // EU visitors, and a banner shown to non-EU visitors who should see none.
  export let autoShow = true;

  let showBanner = false;
  // Second layer - the preferences panel (Essential + one marketing toggle).
  let showPreferences = false;
  let mounted = false;
  let marketing = false;
  // The single non-essential category toggle. Defaults OFF - no non-essential
  // cookies without an explicit opt-in.
  let marketingToggle = false;
  // Where the final choice was made. Attached to cookie_consent_choice so we
  // can separate banner "Accept all" from panel Save/Reject in PostHog.
  let consentVia = "banner";

  // ab-config.js gates the 365-day ph_distinct_id identifier cookie on this
  // marker, because localStorage is invisible to the server (hub ledger
  // 2026-09-09-012). Same cookie name and attributes as SOLDR and FLOSSR.
  function setConsentCookie() {
    document.cookie = "marketing_consent=true; path=/; max-age=31536000; secure; samesite=lax";
  }

  function expireCookie(name) {
    // Expire on the exact host and on the parent domain: the pixel cookies
    // (_fbp, _ttp, _rdt_uuid) are set domain-wide, so a host-only expiry
    // leaves them in place.
    document.cookie = `${name}=; path=/; max-age=0`;
    const host = location.hostname.split(".").slice(-2).join(".");
    document.cookie = `${name}=; path=/; max-age=0; domain=.${host}`;
  }

  onMount(() => {
    if (!browser) return;
    // Mark mounted so toggleBanner() can render this instance on demand,
    // but only the autoShow instance runs the first-visit consent logic.
    mounted = true;
    if (!autoShow) return;

    const cookieConsent = localStorage.getItem("cookieConsent");
    if (cookieConsent) {
      // An explicit choice always wins and needs no geo lookup.
      marketing = localStorage.getItem("marketing") === "true";
      marketingConsentStore.set(marketing);
      return;
    }

    // No stored choice yet, so the region decides. `requiresCookieConsent`
    // arrives from the layout, which is baked at build time on prerendered
    // routes and so always reads `true` there. Ask the server for the real
    // answer before deciding; see $lib/utils/consent-region.js.
    resolveConsentRegion(requiresCookieConsent).then((needsConsent) => {
      // Correct the flag the Meta CAPI helper reads, which the layout seeds
      // from the same untrustworthy build-time value.
      window.__REQUIRES_CONSENT = needsConsent;

      if (needsConsent) {
        // Delay banner appearance by 1.5s - research shows this improves acceptance.
        setTimeout(() => {
          showBanner = true;
          // Denominator for consent rate: shown → accept_all / essential_only
          // / ignored. Fires pre-consent (cookieless memory mode). No PII.
          captureEvent("cookie_banner_shown");
        }, 1500);
        return;
      }

      // Opt-out region (banner never shown): marketing is allowed by
      // default - UNLESS the browser sends a Global Privacy Control opt-out
      // signal, which we honor as a decline (legally recognized in CA/CO/CT
      // and aligned with best practice everywhere).
      // Persist the flag because every pixel/CAPI/UTM consumer reads
      // localStorage directly. Deliberately do NOT set "cookieConsent", so if
      // the same device later loads in a consent-required region the banner
      // still shows and an explicit choice overrides this default.
      const gpcOptOut = navigator.globalPrivacyControl === true;
      const allowMarketing = !gpcOptOut;
      localStorage.setItem("marketing", allowMarketing ? "true" : "false");
      if (allowMarketing) setConsentCookie(); // marketing_consent=true for the server
      marketing = allowMarketing;
      marketingConsentStore.set(allowMarketing);
    });
  });

  export function toggleBanner() {
    showBanner = !showBanner;
    showPreferences = false;
  }

  function openPreferences() {
    // Reflect the current stored choice when reopening.
    marketingToggle = localStorage.getItem("marketing") === "true";
    showPreferences = true;
    captureEvent("cookie_preferences_opened");
  }

  function savePreferences() {
    consentVia = "panel_save";
    // A single toggle: on == full marketing consent, off == essential only.
    if (marketingToggle) {
      acceptAll();
    } else {
      acceptEssentials();
    }
  }

  function acceptAll() {
    // Marketing consent rate is the single biggest lever on Meta CAPI/pixel
    // coverage (no consent → those pipes send nothing), so measure it. No PII.
    captureEvent("cookie_consent_choice", { choice: "accept_all", via: consentVia });
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("marketing", "true");
    setConsentCookie(); // marketing_consent=true for the server
    marketing = true;
    marketingConsentStore.set(true);
    showBanner = false;
    showPreferences = false;
  }

  function acceptEssentials() {
    const wasMarketing = localStorage.getItem("marketing") === "true";
    // sendBeacon transport so the event survives the reload below (downgrade).
    captureBeacon("cookie_consent_choice", { choice: "essential_only", via: consentVia });
    localStorage.setItem("cookieConsent", "declined");
    localStorage.setItem("marketing", "false");
    marketing = false;
    marketingConsentStore.set(false);
    showBanner = false;
    showPreferences = false;

    // Withdrawal (GDPR Art. 7(3)) must also REMOVE what was collected while
    // consent was on, not only stop future writes: otherwise the device keeps
    // every id needed to re-link the visitor. Always run the cleanup - the
    // keys can exist even when `marketing` already reads false.

    // Ad click IDs (fbclid, gclid, ttclid...) and their expiry stamps, stored
    // by +layout.svelte as tracking_<key> / tracking_<key>_expiry. The
    // layout's own expiry sweep only runs WITH consent, so a revoked visitor
    // would otherwise keep them forever.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith("tracking_")) localStorage.removeItem(key);
    }

    // Server marker, the identifier it allowed, and the pixel cookies.
    expireCookie("marketing_consent");
    expireCookie("ph_distinct_id");
    expireCookie("_fbp");
    expireCookie("_fbc");
    expireCookie("_ttp");
    expireCookie("_tt_enable_cookie");
    // TikTok's session ids, seen surviving withdrawal on production after #84.
    expireCookie("ttcsid");
    expireCookie(`ttcsid_${TIKTOK_PIXEL_ID}`);
    expireCookie("_rdt_uuid");

    if (wasMarketing) {
      // Stop PostHog, wipe its persisted identity and recording state and
      // drop to cookieless mode (set_config alone does not delete existing
      // persistence), then reload: injected third-party scripts cannot be
      // removed from the DOM.
      if (window.posthog) {
        window.posthog.opt_out_capturing();
        if (typeof window.posthog.reset === "function") {
          window.posthog.reset();
        }
        window.posthog.set_config({
          persistence: "memory",
          disable_session_recording: true,
          autocapture: false,
        });
      }

      // Neuter the loaded pixel globals so nothing fires between here and
      // the reload.
      if (window.fbq) window.fbq = function () {};
      if (window.ttq) window.ttq = { track() {}, page() {} };
      if (window.rdt) window.rdt = function () {};

      window.location.reload();
    }
  }
</script>

<!-- Steps aside while the €10-off survey is open: on phones the survey is
     taller than the screen and its Next sat under this sheet, so a tap meant
     for Next pressed Accept (hub ledger 2026-11-03-135). Only the rendering
     waits; nothing is decided for the visitor, and the banner is back,
     unanswered, the moment the survey closes. -->
{#if mounted && showBanner && !$signup10Open}
  <div class="cookie-banner" data-testid="cookie-banner" role="dialog" aria-label="Cookie consent">
    <div class="cookie-inner">
      {#if !showPreferences}
        <!-- Layer 1: text, then Preferences (secondary) + Accept (primary) -->
        <div class="cookie-text">
          <p class="cookie-title cookie-title--intro">We use cookies</p>
          <p class="cookie-desc">
            We use cookies to personalise content and ads, and to analyse our traffic.
            <a href="/policies/cookies/" class="cookie-link">Cookie policy</a>
          </p>
        </div>
        <div class="cookie-buttons">
          <button
            class="prefs-btn"
            data-testid="cookie-preferences"
            on:click|stopPropagation={openPreferences}
          >Preferences</button>
          <button
            class="accept-btn"
            data-testid="cookie-accept-all"
            on:click|stopPropagation={() => { consentVia = "banner"; acceptAll(); }}
          >Accept</button>
        </div>
      {:else}
        <!-- Layer 2: preferences panel with an explicit one-click Reject all -->
        <div class="cookie-text">
          <p class="cookie-title">Cookie preferences</p>
          <p class="cookie-desc">
            Choose which cookies we can use.
            <a href="/policies/cookies/" class="cookie-link">Cookie policy</a>
          </p>
        </div>
        <div class="cookie-prefs" data-testid="cookie-preferences-panel">
          <div class="pref-row">
            <div class="pref-label">
              <span>Essential</span>
              <small>Required for the site to work</small>
            </div>
            <span class="pref-always">Always on</span>
          </div>
          <div class="pref-row">
            <div class="pref-label">
              <span>Marketing &amp; analytics</span>
              <small>Helps us measure and improve the store</small>
            </div>
            <label class="switch">
              <input
                type="checkbox"
                data-testid="cookie-toggle-marketing"
                aria-label="Marketing and analytics cookies"
                bind:checked={marketingToggle}
              />
              <span class="slider"></span>
            </label>
          </div>
        </div>
        <div class="cookie-buttons cookie-buttons--prefs">
          <div class="prefs-secondary">
            <button
              class="reject-btn"
              data-testid="cookie-reject-all"
              on:click|stopPropagation={() => { consentVia = "panel_reject"; acceptEssentials(); }}
            >Reject all</button>
            <button
              class="save-btn"
              data-testid="cookie-save"
              on:click|stopPropagation={savePreferences}
            >Save</button>
          </div>
          <button
            class="accept-btn"
            data-testid="cookie-accept-all"
            on:click|stopPropagation={() => { consentVia = "panel"; acceptAll(); }}
          >Accept all</button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  // GLORB cookie banner - light card, Montreal type, black/white with the
  // mission-page blue (#46cdf6) as the single accent. Two-layer consent
  // (Accept / Preferences → marketing toggle + Reject all), styled after the
  // SOLDR reference but rebranded for GLORB.
  $glorb-blue: #46cdf6;

  .cookie-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fff;
    color: #000;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-family: "Montreal regular", system-ui, -apple-system, sans-serif;
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  // On mobile the banner is a full-width bottom sheet - lift it above the
  // product-page sticky checkout bar so both stay tappable.
  @media (max-width: 699.98px) {
    :global(body:has(.sticky-add-to-cart)) .cookie-banner {
      bottom: 3rem;
    }
  }

  .cookie-inner {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem var(--padding, 1rem);
    max-width: 1400px;
    margin: 0 auto;
  }

  .cookie-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .cookie-title {
    color: #000;
    font-size: 0.9rem;
    font-weight: 700;
    margin: 0;
  }

  .cookie-desc {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.8rem;
    line-height: 1.5;
    margin: 0;
  }

  .cookie-link {
    color: rgba(0, 0, 0, 0.6);
    text-decoration: underline;
    white-space: nowrap;

    &:hover {
      color: $glorb-blue;
    }
  }

  // Phones: a compact sheet, the same as SOLDR's. At 160px the sheet took a
  // fifth of the first screen and hid the homepage hero's BUY NOW on every
  // phone (hub ledger 2026-09-28-086, "Phones"). Same content, less height:
  // the sentence already opens with "We use cookies", so the first layer's
  // title goes (the preferences panel keeps its own); smaller text and
  // padding; the buttons keep their 44px tap height and stay side by side.
  @media (max-width: 699.98px) {
    .cookie-inner {
      gap: 0.5rem;
      padding: 0.625rem var(--padding, 1rem);
    }

    .cookie-title--intro {
      display: none;
    }

    .cookie-desc {
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }

  // --- Preferences panel ---
  .cookie-prefs {
    display: flex;
    flex-direction: column;
  }

  .pref-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    &:first-child {
      border-top: none;
    }
  }

  .pref-label {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;

    span {
      color: #000;
      font-size: 0.82rem;
      font-weight: 600;
    }

    small {
      color: rgba(0, 0, 0, 0.5);
      font-size: 0.72rem;
    }
  }

  .pref-always {
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }

  // Toggle switch
  .switch {
    position: relative;
    display: inline-block;
    width: 42px;
    height: 24px;
    flex-shrink: 0;

    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  .slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 24px;
    transition: background 0.2s;

    &::before {
      content: "";
      position: absolute;
      height: 18px;
      width: 18px;
      left: 3px;
      top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
  }

  .switch input:checked + .slider {
    background: $glorb-blue;
  }

  .switch input:checked + .slider::before {
    transform: translateX(18px);
  }

  .switch input:focus-visible + .slider {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  // --- Buttons ---
  .cookie-buttons {
    display: flex;
    flex-direction: row;
    gap: 0.6rem;
  }

  .cookie-buttons--prefs {
    flex-direction: column;
  }

  .prefs-secondary {
    display: flex;
    flex-direction: row;
    gap: 0.6rem;
  }

  .accept-btn,
  .prefs-btn,
  .reject-btn,
  .save-btn {
    flex: 1;
    padding: 0.65rem 1rem;
    cursor: pointer;
    font-family: "Montreal regular", system-ui, -apple-system, sans-serif;
    font-size: 0.85rem;
    font-weight: 600;
    min-height: 44px;
    border-radius: var(--border-radius-small, 10px);
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
    transition: opacity 0.2s, border-color 0.2s, background 0.2s;
    white-space: nowrap;
  }

  // Primary / emphasised action - solid black (matches site buttons + modal CTA).
  .accept-btn {
    background: #000;
    color: #fff;
    border: 2px solid #000;

    &:hover {
      background: #1a1a1a;
    }
  }

  // Secondary / outlined actions (Preferences, Reject all, Save).
  .prefs-btn,
  .reject-btn,
  .save-btn {
    background: transparent;
    color: #000;
    border: 2px solid rgba(0, 0, 0, 0.25);

    &:hover {
      border-color: #000;
    }
  }

  button:focus-visible {
    outline: 2px solid $glorb-blue;
    outline-offset: 2px;
  }

  @media (min-width: 700px) {
    .cookie-banner {
      left: auto;
      right: 2rem;
      bottom: 2rem;
      width: 340px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: var(--border-radius-small, 10px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    }

    .cookie-inner {
      flex-direction: column;
      gap: 1rem;
      padding: 1.5rem;
    }

    .cookie-buttons {
      flex-direction: column;
      gap: 0.6rem;
    }

    .accept-btn,
    .prefs-btn,
    .reject-btn,
    .save-btn {
      width: 100%;
    }
  }
</style>
